import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';

// One shared standard for Administration Hub tables:
// 3-state column sorting (asc -> desc -> off), per-column text filtering,
// a live matching-record count and Clear Filters. Mirrors Personnel Master.

const getVal = (row, key) => {
  if (typeof key === 'function') return key(row);
  return key.split('.').reduce((o, k) => (o == null ? o : o[k]), row);
};

export const applySort = (list, key, dir) => {
  if (!key || !dir) return list;
  const val = (o) => {
    const v = getVal(o, key);
    if (v == null) return '';
    if (typeof v === 'number') return v;
    if (typeof v === 'boolean') return v ? 1 : 0;
    return String(v).toLowerCase();
  };
  const s = [...list].sort((a, b) => { const x = val(a), y = val(b); return x < y ? -1 : x > y ? 1 : 0; });
  return dir === -1 ? s.reverse() : s;
};

// Sortable header cell. `k` may be a field name/dotted path (also used for filtering).
export const SortTh = ({ label, k, std, testid, className = '', sortKey }) => {
  const key = sortKey || k;
  const active = std.sort.key === key;
  const next = () => std.setSort((s) => (s.key !== key ? { key, dir: 1 } : s.dir === 1 ? { key, dir: -1 } : { key: null, dir: 0 }));
  return (
    <th className={`px-3 py-2 text-left text-[11px] font-bold ${className}`}>
      <button onClick={next} data-testid={testid || `sort-${key}`} className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-[#0E2A47]">
        {label}<span className="text-[10px]">{active ? (std.sort.dir === 1 ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
};

// A filter <input> cell to place in a second header row, aligned to a column.
export const FilterTh = ({ std, field, placeholder = 'Filter…', testid, className = '' }) => (
  <th className={`px-2 pb-2 ${className}`}>
    {field ? (
      <input value={std.filters[field] || ''} onChange={(e) => std.setFilter(field, e.target.value)}
        placeholder={placeholder} data-testid={testid || `filter-${field}`}
        className="w-full min-w-[80px] rounded border border-gray-200 px-2 py-1 text-xs font-normal normal-case" />
    ) : null}
  </th>
);

// Header bar with matching-record count + Clear Filters.
export const TableToolbar = ({ std, total, noun = 'record', extra = null, idPrefix = 'table' }) => (
  <div className="mb-2 flex flex-wrap items-center gap-3 text-sm text-slate-500">
    <span data-testid={`${idPrefix}-match-count`}>{std.matchCount} of {total} {std.matchCount === 1 ? noun : `${noun}s`}</span>
    {extra}
    {std.filtersActive && (
      <button onClick={std.clear} data-testid={`${idPrefix}-clear-filters`}
        className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200">
        <X size={12} /> Clear Filters
      </button>
    )}
  </div>
);

export function useTableStandard(rows, { defaultSort, filterKeys = [] } = {}) {
  const [sort, setSort] = useState(defaultSort || { key: null, dir: 0 });
  const [filters, setFilters] = useState({});
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clear = () => { setSort({ key: null, dir: 0 }); setFilters({}); };

  const view = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) => v);
    const filtered = !active.length ? rows : rows.filter((r) =>
      active.every(([k, v]) => String(getVal(r, k) ?? '').toLowerCase().includes(String(v).toLowerCase())));
    return applySort(filtered, sort.key, sort.dir);
  }, [rows, filters, sort]);

  const filtersActive = !!sort.key || Object.values(filters).some(Boolean);
  return { sort, setSort, filters, setFilter, clear, view, matchCount: view.length, filtersActive, filterKeys };
}
